import { Locale } from '../../../constants';

// Mirrors backend `parse_creative` (apps/core/helpers.py):
// project_name_creative_id_creative_base_id_gender_locale_branch_customer_creative_type_idea_size[tail]
// v1 keeps everything after `size` as `tail`; v2 names a trailing `_flow` segment.
// Each field is null when its segment is missing or does not match the grammar.
export interface ParsedCreativeName {
  projectName: string | null;
  creativeId: string | null;
  creativeBaseId: string | null;
  gender: string | null;
  locale: string | null;
  branch: string | null;
  customer: string | null;
  creativeType: string | null;
  idea: string | null;
  size: string | null;
  tail: string | null;
  flow: string | null;
}

const GENDERS = new Set(['MA', 'WO', 'MW']);
const KNOWN_LOCALES = new Set(Object.values(Locale).map((locale) => locale.toUpperCase()));

const word = (segment: string | undefined): string | null => segment?.trim() || null;
const numeric = (segment: string | undefined): string | null =>
  segment && /^\d+$/.test(segment) ? segment : null;
const oneOf =
  (allowed: Set<string>) =>
  (segment: string | undefined): string | null => {
    const value = segment?.trim().toUpperCase() ?? '';
    return allowed.has(value) ? value : null;
  };

export const parseCreativeName = (creativeName: string | undefined): ParsedCreativeName => {
  const [
    projectName,
    creativeId,
    creativeBaseId,
    gender,
    locale,
    branch,
    customer,
    creativeType,
    idea,
    size,
    ...rest
  ] = creativeName?.split('_') ?? [];

  return {
    projectName: word(projectName),
    creativeId: numeric(creativeId),
    creativeBaseId: numeric(creativeBaseId),
    gender: oneOf(GENDERS)(gender),
    locale: oneOf(KNOWN_LOCALES)(locale),
    branch: word(branch),
    customer: word(customer),
    creativeType: word(creativeType),
    idea: word(idea),
    size: word(size),
    tail: rest.length ? `_${rest.join('_')}` : null,
    flow: rest.length === 1 ? word(rest[0]) : null,
  };
};

export const parseCreativeLocale = (creativeName: string | undefined): string | null =>
  parseCreativeName(creativeName).locale;
